import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DescriptionIcon from "@mui/icons-material/Description";
import dayjs from "dayjs";
import THUMBNAIL from "../../assets/images/thumbnailForm.png";

const AlreadyCreatedForms = ({
  open,
  onClose,
  onCreateNew,
  onEditForm,
  onDuplicateForm,
  onDeleteForm,
  onUseForm,
  existingForms = [], // Array of existing forms
  selectedPreAssessmentId,
  onRemove,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);

  const handleMenuClick = (event, formId) => {
    setAnchorEl(event.currentTarget);
    setSelectedFormId(formId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFormId(null);
  };

  const handleEdit = () => {
    onEditForm?.(selectedFormId);
    handleMenuClose();
  };

  const handleDuplicate = () => {
    onDuplicateForm?.(selectedFormId);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDeleteForm?.(selectedFormId);
    handleMenuClose();
  };

  const handleUseForm = (formId) => {
    onUseForm?.(formId);
  };

  const filteredForms = existingForms?.filter((form) => {
    const name = form?.form_name || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          width: { xs: "95vw", md: "60vw" },
          height: { xs: "95vh", md: "75vh" },
          overflow: "auto",
          maxWidth: "none",
          maxHeight: "none",
          p: 2,
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                border: 1,
                borderColor: "#999",
                borderRadius: "50%",
                width: 32,
                height: 32,
                justifyContent: "center",
              }}
            >
              <ArrowBackIosIcon
                sx={{ width: "16px", height: "16px", ml: 0.6 }}
              />
            </IconButton>
            <Typography
              variant="h5"
              fontWeight={600}
              fontFamily="Outfit"
              sx={{ ml: 1 }}
              color="#42545E"
            >
              Pre-Assessment Forms
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: 1,
              borderColor: "#999",
              borderRadius: "50%",
              width: 32,
              height: 32,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        <Typography
          variant="h6"
          fontWeight={400}
          fontFamily="Outfit"
          sx={{ mb: 3 }}
          color="#354651"
        >
          My Forms
        </Typography>
        <Box
          sx={{
            border: 1,
            borderColor: "#CFCFCF",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <TextField
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: "400px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px",
                  height: "40px",
                  fontFamily: "Outfit",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#9048FB" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateNew}
              sx={{
                bgcolor: "#9048FB",
                "&:hover": { bgcolor: "#7035d8" },
                fontFamily: "Outfit",
                borderRadius: "24px",
                textTransform: "none",
                px: 3,
              }}
            >
              Create New
            </Button>
          </Box>

          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: 2,
              }}
            >
              {filteredForms?.map((form) => (
                <Card
                  key={form?.id}
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.2s ease",
                    minHeight: "350px",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    {/* Form Preview Image */}
                    <Box
                      sx={{
                        height: "250px",
                        bgcolor: "#f8f4ff",
                        borderRadius: "12px 12px 0 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        backgroundImage: `url(${
                          THUMBNAIL || "/api/placeholder/300/250"
                        })`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "top",
                      }}
                    />

                    {/* Form Details */}
                    <Box sx={{ p: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={500}
                          fontFamily="Outfit"
                          sx={{ fontSize: "16px" }}
                          color="#0C0E0F"
                        >
                          {form?.form_name}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <DescriptionIcon
                            sx={{
                              width: "16px",
                              height: "16px",
                              color: "#9048FB",
                              mt: 0.5,
                            }}
                          />

                          <Box display="flex" flexDirection="column">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontFamily="Outfit"
                            >
                              Last modified:{" "}
                              {dayjs(form?.updated_at).format("MMM D, YYYY")}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontFamily="Outfit"
                            >
                              {form.form_data?.questions?.length} questions
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {selectedPreAssessmentId === form?.id ? (
                            <Chip
                              label="Remove"
                              onClick={() => onRemove()}
                              sx={{
                                bgcolor: "#f44336",
                                color: "white",
                                fontFamily: "Outfit",
                                fontSize: "12px",
                                height: "24px",
                                "&:hover": {
                                  bgcolor: "#d32f2f",
                                },
                              }}
                            />
                          ) : (
                            <Chip
                              label="Use Form"
                              clickable
                              onClick={() => handleUseForm(form?.id)}
                              sx={{
                                bgcolor: "#9048FB",
                                color: "white",
                                fontFamily: "Outfit",
                                fontSize: "12px",
                                height: "24px",
                                "&:hover": {
                                  bgcolor: "#7035d8",
                                },
                              }}
                            />
                          )}

                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, form.id)}
                            sx={{
                              border: 1,
                              borderColor: "#999",
                              borderRadius: "50%",
                              width: 30,
                              height: 30,
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {filteredForms?.length === 0 && searchTerm && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
                textAlign: "center", // Optional for better alignment
                px: 2, // Optional padding for smaller screens
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                fontFamily="Outfit"
                sx={{
                  wordBreak: "break-word", // Force long text to break
                  overflowWrap: "break-word", // Handles very long words
                  maxWidth: "100%", // Prevents overflow
                }}
              >
                No forms found matching "{searchTerm}"
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleEdit} sx={{ fontFamily: "Outfit" }}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate} sx={{ fontFamily: "Outfit" }}>
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            fontFamily: "Outfit",
            color: "#FF2226",
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default AlreadyCreatedForms;
